import { NgFor, NgIf } from '@angular/common';
import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import {
  NbButtonModule,
  NbCardModule,
  NbDateService,
  NbDialogRef,
  NbDialogService,
  NbIconModule,
  NbSpinnerModule,
  NbToastrService,
  NbTooltipModule
} from '@nebular/theme';
import { Store } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { ViewContentComponent } from 'src/app/features/content/content-table/view-content/view-content.component';
import { ViewInsightComponent } from 'src/app/features/insights/insights-table/view-insight/view-insight.component';
import { InsightsState } from 'src/app/features/insights/redux';
import { ViewSurveyComponent } from 'src/app/features/surveys/surveys-table/view-survey/view-survey.component';
import { Campaign } from 'src/app/models/campaign';
import { Content } from 'src/app/models/content';
import { Survey } from 'src/app/models/survey';
import { SharedModule } from 'src/app/shared/shared.module';
import { ENVIRONMENT } from 'src/environments/environment';
import { Insight } from '../../../../../models/insight';
import { CampaignService } from '../../../services/campaign.service';
/*
This component acts as a handler for promoting campagins
*/

@Component({
  selector: 'app-campaign-promotion-component',
  templateUrl: './campaign-promotion-component.component.html',
  styleUrls: ['./campaign-promotion-component.component.scss'],
  imports: [NbCardModule, NgIf, NbButtonModule, NbIconModule, NbTooltipModule, NgFor, NbSpinnerModule, SharedModule]
})
export class CampaignPromotionComponent implements OnInit, OnDestroy {
  public activeInsight$: Observable<Insight[] | undefined> = inject(Store).select(InsightsState.insightsActive);
  private unsubscribe = new Subject<void>();

  @Input() public campaign!: Campaign;
  @Input() public insights: Insight[] = [];
  @Input() public content?: Content;
  @Input() public username!: string;
  @Input() public campaign_trigger!: Insight;
  @Input() public surveys: Survey[] = [];
  public results: any = false;
  public errors: any = false;
  public selectedSurveys: { id: string; name?: string }[] = [];

  public target_environment =
    ENVIRONMENT.name === 'dev'
      ? $localize`Stage Environment`
      : ENVIRONMENT.name === 'stage'
        ? $localize`Production Environment`
        : ENVIRONMENT.name === 'tkstage'
          ? $localize`TK Production Environment`
          : '';

  constructor(
    protected ref: NbDialogRef<CampaignPromotionComponent>,
    public service: CampaignService,
    private dialogService: NbDialogService,
    private toastrService: NbToastrService,
    protected dateService: NbDateService<Date>
  ) {}

  public ngOnInit() {
    if (this.campaign.responses) {
      this.campaign.responses.forEach((item) => {
        const obj = { name: item.name, id: item.id };
        this.selectedSurveys.push(obj);
      });
    }
    if (this.insights.length == 0) {
      this.activeInsight$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
        if (data) {
          this.insights = data;
        }
      });
    }
    if (!this.campaign_trigger) {
      const found = this.insights.find((element) => element.id === Number(this.campaign.trigger_id));
      if (found) {
        this.campaign_trigger = found;
      }
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public clickedPromote() {
    this.postCampaignAndInsight();
  }

  public viewInsight(event: number | undefined) {
    const found = this.insights.find((element) => element.id === event);
    if (found) {
      this.dialogService.open(ViewInsightComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          insight: found
        }
      });
    } else {
      this.toastrService.show(status, $localize`Insight with this ID does not exist`, {
        status: 'danger'
      });
    }
  }

  public viewContent() {
    const found = this.content;
    if (found) {
      this.dialogService.open(ViewContentComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          content: found
        }
      });
    } else {
      this.toastrService.show(status, $localize`Content with this ID does not exist`, {
        status: 'danger'
      });
    }
  }

  public viewSurvey(survey_id: string) {
    const found = this.surveys.find((element) => element.survey_id === survey_id);
    if (found) {
      this.dialogService.open(ViewSurveyComponent, {
        context: {
          survey: Survey.copy(found) as Survey
        }
      });
    } else {
      this.toastrService.show(status, $localize`Survey with this ID does not exist`, {
        status: 'danger'
      });
    }
  }

  public clickedCancel() {
    this.service.promotionError = false;
    this.service.promotionResponse = false;
    this.service.promotionSpinner = false;
    this.ref.close();
  }

  public postCampaignAndInsight() {
    this.service.postPromoteCampaign(this.campaign.id, this.username);
  }
}
